import { useEffect, useState, type FormEvent } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { resetPasswordSchema } from "@tc/shared";
import { Button, Icon, LogoIcon, PasswordInput } from "@/components/ui";
import { adminApi, ApiError } from "@/features/clubs/api";

type Phase = "loading" | "form" | "done" | "invalid";

/**
 * Public set-new-password page (reached from the reset email link, /reset-password/:token).
 * Mirrors AcceptInvitePage: validate the token on mount, let the user pick a new password, then
 * send them to sign in. Sits beside /login — outside the role shells — and renders in the
 * globally-initialized language. The reset revokes existing sessions server-side, so after this
 * the user signs in fresh with the new password.
 */
export function ResetPasswordPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { token = "" } = useParams();

  const [phase, setPhase] = useState<Phase>("loading");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    let cancelled = false;
    adminApi
      .validateResetToken(token)
      .then(() => {
        if (!cancelled) setPhase("form");
      })
      .catch(() => {
        if (!cancelled) setPhase("invalid");
      });
    return () => {
      cancelled = true;
    };
  }, [token]);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    const parsed = resetPasswordSchema.safeParse({ password });
    if (!parsed.success) {
      setError(parsed.error.issues[0]?.message ?? t("auth.reset.error"));
      return;
    }
    setSubmitting(true);
    try {
      await adminApi.resetPassword(token, parsed.data);
      setPhase("done");
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t("auth.reset.error"));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <main className="auth-stage">
      <div className="auth-shell">
        <div className="auth-head">
          <div className="auth-wordmark">
            <LogoIcon style={{ height: 38 }} />
            <div style={{ textAlign: "left" }}>
              <div className="wm-name">Travel Connection</div>
              <div className="auth-tagline">{t("auth.tagline")}</div>
            </div>
          </div>
        </div>

        <div className="auth-card">
          {phase === "loading" && <p className="auth-sub">{t("auth.reset.loading")}</p>}

          {phase === "invalid" && (
            <>
              <h1>{t("auth.reset.title")}</h1>
              <div className="auth-alert" style={{ marginTop: 20 }} role="alert">
                <Icon name="alert" size={17} className="aa-ic" />
                <div className="aa-body">{t("auth.reset.invalid")}</div>
              </div>
              <div className="auth-foot" style={{ marginTop: 18 }}>
                <button type="button" className="link" onClick={() => navigate("/forgot-password")}>
                  {t("auth.forgot")}
                </button>
              </div>
            </>
          )}

          {phase === "form" && (
            <>
              <h1>{t("auth.reset.title")}</h1>
              <p className="auth-sub">{t("auth.reset.subtitle")}</p>

              {error && (
                <div className="auth-alert" style={{ marginTop: 20 }} role="alert">
                  <Icon name="alert" size={17} className="aa-ic" />
                  <div className="aa-body">{error}</div>
                </div>
              )}

              <form className="auth-form" onSubmit={onSubmit} noValidate>
                <div>
                  <label className="auth-field-label" htmlFor="reset-password">
                    {t("auth.reset.newPassword")}
                  </label>
                  <PasswordInput
                    icon="lock"
                    id="reset-password"
                    autoFocus
                    value={password}
                    onChange={setPassword}
                    autoComplete="new-password"
                    showLabel={t("auth.showPassword")}
                    hideLabel={t("auth.hidePassword")}
                  />
                  <div className="field-hint" style={{ marginTop: 6 }}>
                    {t("auth.reset.passwordHint")}
                  </div>
                </div>

                <Button type="submit" size="lg" full disabled={submitting}>
                  {submitting ? t("auth.signingIn") : t("auth.reset.submit")}
                </Button>
              </form>
            </>
          )}

          {phase === "done" && (
            <>
              <h1>{t("auth.reset.success")}</h1>
              <p className="auth-sub">{t("auth.reset.successBody")}</p>
              <Button size="lg" full style={{ marginTop: 22 }} onClick={() => navigate("/login")}>
                {t("auth.reset.goToLogin")}
              </Button>
            </>
          )}
        </div>
      </div>

      <div className="auth-pagefoot">
        <span>© {new Date().getFullYear()} Travel Connection Spain</span>
      </div>
    </main>
  );
}
